import React from 'react';
import {
  FolderKanban,
  Package2,
  RefreshCw,
  Rocket,
  RotateCcw,
  Tag,
  Trash2,
  X,
} from 'lucide-react';

import { Button } from '@/Modules/Core/Components/shadcn/button';
import { SplitButton } from '@/Modules/Core/Components/shadcn/split-button';

const ACCUMULATIVE_OPTION = {
  id: 'accumulative',
  label: 'Accumulative Rollout',
  icon: Package2,
};

const RolloutCatalogHeader = ({
  loading,
  projectName,
  selectedCount,
  versionName,
  onDeleteSelected,
  onRecreateSelected,
  onClearSelection,
  onRefresh,
  onCreateRollout,
  onCreateAccumulative,
  canDeleteSelected = true,
  canRecreateSelected = true,
  canCreateRollout = true,
}) => (
  <div className="flex flex-col gap-4 border-b border-border/30 pb-5 lg:flex-row lg:items-center lg:justify-between">
    <div className="flex min-h-9 flex-col justify-center gap-2">
      {projectName || versionName || selectedCount > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {projectName ? (
            <div className="inline-flex max-w-full items-center gap-2 rounded-xl border border-border/45 bg-background/70 px-3 py-1.5 text-[12px] text-foreground">
              <FolderKanban className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="text-muted-foreground">Project</span>
              <span className="max-w-[220px] truncate font-medium text-foreground">
                {projectName}
              </span>
            </div>
          ) : null}

          {versionName ? (
            <div className="inline-flex max-w-full items-center gap-2 rounded-xl border border-border/45 bg-background/70 px-3 py-1.5 text-[12px] text-foreground">
              <Tag className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="text-muted-foreground">Version</span>
              <span className="max-w-[180px] truncate font-medium text-foreground">
                {versionName}
              </span>
            </div>
          ) : null}

          {selectedCount > 0 ? (
            <div className="inline-flex items-center rounded-xl border border-primary/20 bg-primary/10 px-3 py-1.5 text-[12px] font-medium text-foreground">
              {selectedCount} selected
            </div>
          ) : null}
        </div>
      ) : null}
    </div>

    <div className="flex flex-wrap items-center justify-end gap-2">
      {selectedCount > 0 && (canDeleteSelected || canRecreateSelected) ? (
        <>
          {canDeleteSelected ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="h-9 rounded-xl px-4 text-[12px] font-medium"
              onClick={onDeleteSelected}
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              {selectedCount > 1 ? `Delete (${selectedCount})` : 'Delete rollout'}
            </Button>
          ) : null}
          {canRecreateSelected ? (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-9 rounded-xl border-transparent bg-primary px-4 text-[12px] font-medium text-primary-foreground shadow-none hover:bg-primary/90 disabled:bg-primary/50 disabled:text-primary-foreground"
              disabled={selectedCount !== 1}
              onClick={onRecreateSelected}
            >
              <RotateCcw className="mr-2 h-3.5 w-3.5" />
              Recreate
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 rounded-xl"
            onClick={onClearSelection}
          >
            <X className="mr-2 h-3.5 w-3.5" />
            Clear
          </Button>
          <span
            aria-hidden="true"
            className="px-1 text-[14px] text-muted-foreground/45"
          >
            |
          </span>
        </>
      ) : null}

      <Button
        type="button"
        size="icon"
        variant="outline"
        className="border-border/40 bg-transparent hover:bg-accent/20"
        aria-label="Refresh rollouts"
        title="Refresh rollouts"
        onClick={onRefresh}
        disabled={loading}
      >
        <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
      </Button>

      {canCreateRollout ? (
        <SplitButton
          primaryAction={
            <>
              <Rocket className="mr-2 h-3.5 w-3.5" />
              <span className="truncate">Create Rollout</span>
            </>
          }
          menuOptions={[ACCUMULATIVE_OPTION]}
          onPrimaryClick={onCreateRollout}
          onSelect={(option) => {
            if (option.id === ACCUMULATIVE_OPTION.id) {
              onCreateAccumulative();
            }
          }}
          className="w-full sm:w-auto"
          primaryClassName="px-4"
        />
      ) : null}
    </div>
  </div>
);

export default RolloutCatalogHeader;
